import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTest } from '../../contexts/TestContext';
import { emotionalAreas } from '../../config/areas';
import ResultsChart from './ResultsChart';
import Consequences from './Consequences';
import Recommendations from './Recommendations';
import LeadForm from '../LeadForm/LeadForm';
import AppointmentButton from './AppointmentButton';
// import EmergencyContact from './EmergencyContact';
// import { leveledContent } from '../../config/consequencesRecommendations';
import './Results.css';

const Results = () => {
  const navigate = useNavigate();
  const { results, selectedArea, resetTest } = useTest();
  const [activeTab, setActiveTab] = useState('overview');
  const [areaInfo, setAreaInfo] = useState(null);
  const [showEmergency, setShowEmergency] = useState(false);
  
  useEffect(() => {
    // Si no hay resultados, redirigir a la selección de área
    if (!results) {
      navigate('/area-selection');
      return;
    }
    
    // Obtener información del área seleccionada
    const foundAreaInfo = emotionalAreas.find(area => area.id === selectedArea);
    if (foundAreaInfo) {
      setAreaInfo(foundAreaInfo);
    }
    
    // Verificar si se debe mostrar el contacto de emergencia
    try {
      // Mostrar contacto de emergencia SOLO para niveles muy altos (≥80%) o con nivel MUY GRAVE
      const isHighSeverity = results.scorePercentage >= 80 || results.level === 'MUY GRAVE';
      setShowEmergency(isHighSeverity);

      // Asegurarse de que el nivel se pase en un formato que coincida con leveledContent
      if (results && results.level) {
        // Crear una copia de los resultados con el nivel convertido
        const levelCopy = results.level;
        // Convertir el nivel a un formato estándar para los componentes de recomendaciones y consecuencias
        if (levelCopy === 'MUY LEVE' || levelCopy === 'LEVE') {
          results.level = 'LEVE';
        } else if (levelCopy === 'MODERADO') {
          results.level = 'MODERADO';
        } else if (levelCopy === 'GRAVE' || levelCopy === 'MUY GRAVE') {
          results.level = 'ALTO';
        }
      }
    } catch (error) {
      console.error('Error al verificar estado de emergencia:', error);
      setShowEmergency(false);
    }
  }, [results, navigate, selectedArea]);
  
  const handleStartOver = () => {
    resetTest();
    navigate('/');
  };
  
  // Si no hay información del área, mostrar un loader
  if (!areaInfo) {
    return <div className="loading">Cargando resultados...</div>;
  }

  // Definir contenido basado en niveles
  const getSufferingLevelLabel = (percentage) => {
    if (percentage < 20) return "Bajo";
    if (percentage < 40) return "Leve";
    if (percentage < 60) return "Moderado";
    if (percentage < 80) return "Significativo";
    return "Intenso";
  };

  const sufferingLevel = getSufferingLevelLabel(results.scorePercentage);
  
  return (
    <div className="results-container">
      {/* Nueva sección de resumen */}
      <motion.div 
        className="results-summary-card"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="summary-header" style={{ borderColor: results.color }}>
          <div className="summary-icon" style={{ backgroundColor: results.color }}>
            <span>📊</span>
          </div>
          <div className="summary-title">
            <h2>Resultado de Tu Evaluación</h2>
            <p className="area-name">{areaInfo.name}</p>
          </div>
          <div className="suffering-badge" style={{ backgroundColor: results.color }}>
            Nivel {sufferingLevel}
          </div>
        </div>
        
        <div className="summary-score">
          <div className="score-circle" style={{ borderColor: results.color }}>
            <span style={{ color: results.color }}>{Math.min(Math.round(results.scorePercentage), 100)}%</span>
          </div>
          <div className="score-description">
            <h3>Nivel de Malestar: {sufferingLevel}</h3>
            <p>{results.description}</p>
          </div>
        </div>
      </motion.div>
      
      {/* Navegación por pestañas */}
      <div className="tabs-navigation">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
          style={activeTab === 'overview' ? { borderColor: results.color } : {}}
        >
          <span className="tab-icon">📊</span>
          Vista General
        </button>
        <button 
          className={`tab-button ${activeTab === 'consequences' ? 'active' : ''}`}
          onClick={() => setActiveTab('consequences')}
          style={activeTab === 'consequences' ? { borderColor: results.color } : {}}
        >
          <span className="tab-icon">⚠️</span>
          Posibles Consecuencias
        </button>
        <button 
          className={`tab-button ${activeTab === 'recommendations' ? 'active' : ''}`}
          onClick={() => setActiveTab('recommendations')}
          style={activeTab === 'recommendations' ? { borderColor: results.color } : {}}
        >
          <span className="tab-icon">✓</span>
          Recomendaciones
        </button>
        <button 
          className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
          onClick={() => setActiveTab('resources')}
          style={activeTab === 'resources' ? { borderColor: results.color } : {}}
        >
          <span className="tab-icon">🎁</span>
          Regalo
        </button>
      </div>
      
      {/* Contenido de las pestañas */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <motion.div 
            className="content-panel modern-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="panel-header modern-header">
              <div className="header-icon" style={{ backgroundColor: results.color + '20' }}>
                <span style={{ color: results.color }}>📊</span>
              </div>
              <h3>Comprende Tu Resultado</h3>
            </div>
            
            <div className="modern-results-container">
              <div className="results-score-section" style={{ borderColor: results.color }}>
                <div className="modern-score">
                  <div className="score-value" style={{ color: results.color, borderColor: results.color }}>
                    <span>{Math.min(Math.round(results.scorePercentage), 100)}%</span>
                  </div>
                  <div className="score-level" style={{ backgroundColor: results.color }}>
                    <span>Nivel {sufferingLevel}</span>
                  </div>
                </div>
                
                <div className="chart-container modern-chart">
                  <ResultsChart 
                    percentage={results.scorePercentage} 
                    color={results.color} 
                    level={results.level}
                  />
                </div>
              </div>
              
              <div className="results-explanation">
                <div className="explanation-header">
                  <h4>¿Qué significa un nivel de malestar {sufferingLevel.toLowerCase()}?</h4>
                </div>
                
                <div className="explanation-card modern-card" style={{ borderTopColor: results.color }}>
                  <p className="explanation-intro">
                    El termómetro emocional mide el nivel de malestar emocional que podrías estar experimentando según tus respuestas. 
                    Un {Math.min(Math.round(results.scorePercentage), 100)}% indica un nivel <strong>{sufferingLevel.toLowerCase()}</strong> de malestar relacionado con {results.level === 'ansiedad' ? 'la ansiedad' : results.level === 'estres' ? 'el estrés' : results.level === 'tristeza' ? 'la tristeza' : results.level === 'autoestima' ? 'la autoestima' : 'tu situación emocional actual'}.
                  </p>
                  
                  <div className="symptoms-container">
                    <h5>Posibles manifestaciones:</h5>
                    <ul className="symptom-list modern-list">
                      {results.level === 'ansiedad' && (
                        <>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Sensación de inquietud o nerviosismo {sufferingLevel === "Bajo" || sufferingLevel === "Leve" ? "ocasional" : "frecuente"}</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Bajo" ? "Ligera" : sufferingLevel === "Leve" ? "Moderada" : "Significativa"} dificultad para concentrarse</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Significativo" || sufferingLevel === "Intenso" ? "Posible interferencia en actividades diarias y relaciones" : "Impacto limitado en tu día a día"}</li>
                          {results.scorePercentage >= 60 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posible presencia de síntomas físicos como tensión muscular o palpitaciones</li>}
                          {results.scorePercentage >= 80 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posible presencia de ataques de pánico o pensamientos catastróficos</li>}
                        </>
                      )}
                      
                      {results.level === 'estres' && (
                        <>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Sensación de presión o agobio {sufferingLevel === "Bajo" || sufferingLevel === "Leve" ? "ocasional" : "persistente"}</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Bajo" ? "Ligera" : sufferingLevel === "Leve" ? "Moderada" : "Significativa"} fatiga mental o física</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Significativo" || sufferingLevel === "Intenso" ? "Posible interferencia en actividades diarias y relaciones" : "Impacto limitado en tu día a día"}</li>
                          {results.scorePercentage >= 60 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posible presencia de irritabilidad o dificultad para relajarse</li>}
                          {results.scorePercentage >= 80 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posibles síntomas de agotamiento extremo o desbordamiento</li>}
                        </>
                      )}
                      
                      {results.level === 'tristeza' && (
                        <>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Sentimientos de tristeza o desánimo {sufferingLevel === "Bajo" || sufferingLevel === "Leve" ? "ocasionales" : "frecuentes"}</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Bajo" ? "Ligera" : sufferingLevel === "Leve" ? "Moderada" : "Significativa"} pérdida de interés o disfrute</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Significativo" || sufferingLevel === "Intenso" ? "Posible interferencia en actividades diarias y relaciones" : "Impacto limitado en tu día a día"}</li>
                          {results.scorePercentage >= 60 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posible presencia de cambios en el apetito o sueño</li>}
                          {results.scorePercentage >= 80 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posibles sentimientos de desesperanza o pensamientos negativos</li>}
                        </>
                      )}
                      
                      {!['ansiedad', 'estres', 'tristeza'].includes(results.level) && (
                        <>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Malestar emocional {sufferingLevel === "Bajo" || sufferingLevel === "Leve" ? "ocasional" : "frecuente"}</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Bajo" ? "Ligera" : sufferingLevel === "Leve" ? "Moderada" : "Significativa"} interferencia en tu bienestar</li>
                          <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> {sufferingLevel === "Significativo" || sufferingLevel === "Intenso" ? "Posible interferencia en actividades diarias y relaciones" : "Impacto limitado en tu día a día"}</li>
                          {results.scorePercentage >= 60 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posible presencia de síntomas que afectan a diferentes áreas</li>}
                          {results.scorePercentage >= 80 && <li><span className="symptom-marker" style={{ backgroundColor: results.color }}></span> Posible afectación significativa de tu calidad de vida</li>}
                        </>
                      )}
                    </ul>
                  </div>
                </div>
                
                <div className="disclaimer-box modern-disclaimer">
                  <div className="disclaimer-icon">ℹ️</div>
                  <p>Esta evaluación es orientativa y no constituye un diagnóstico profesional. Para una valoración completa, recomendamos consultar con un profesional de la salud mental.</p>
                </div>
              </div>
            </div>
            
            <div className="overview-actions modern-actions">
              <div className="action-buttons-container">
                <AppointmentButton className="primary-action" />
                <button 
                  className="secondary-action modern-secondary"
                  onClick={() => {
                    setActiveTab('resources');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Ver Recursos <span className="action-icon">🎁</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
        
        {activeTab === 'consequences' && (
          <motion.div 
            className="content-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="panel-header">
              <h3>Posibles Consecuencias a Medio Plazo</h3>
              <p className="panel-description">
                Si {areaInfo.name === 'Ansiedad' ? 'la ansiedad' : areaInfo.name === 'Estrés' ? 'el estrés' : areaInfo.name === 'Tristeza' ? 'la tristeza' : areaInfo.name === 'Autoestima' ? 'los problemas de autoestima' : areaInfo.name === 'Ruptura' ? 'el duelo por la ruptura' : areaInfo.name === 'Insomnio' ? 'el insomnio' : 'este malestar'} se mantiene sin abordar a lo largo del tiempo, podría generar una desregulación emocional, pensamientos negativos acelerados y algunas de estas consecuencias:
              </p>
            </div>
            
            <Consequences 
              areaId={selectedArea}
              level={results.level}
              color={results.color}
            />
            
            <div className="panel-actions">
              <div className="action-buttons-container">
                <AppointmentButton className="primary-action" />
                <button 
                  className="secondary-action"
                  onClick={() => {
                    setActiveTab('resources');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Ver Regalo 🎁
                </button>
              </div>
            </div>
          </motion.div>
        )}
        
        {activeTab === 'recommendations' && (
          <motion.div 
            className="content-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="panel-header">
              <h3>Recomendaciones Personalizadas</h3>
              <p className="panel-description">
                Basado en tu evaluación, estas estrategias podrían ayudarte a reducir tu nivel de malestar:
              </p>
            </div>
            
            <Recommendations 
              areaId={selectedArea}
              level={results.level}
              color={results.color}
            />
            
            {/* La advertencia se muestra ya en el componente Recommendations */}
            
            <div className="panel-actions">
              <AppointmentButton className="primary-action" />
            </div>
          </motion.div>
        )}
        
        {activeTab === 'resources' && (
          <motion.div 
            className="content-panel resources-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="panel-header regalo-header">
              <div className="gift-icon-wrapper">
                <span className="gift-icon">🎁</span>
              </div>
              <h3>RECURSOS EXCLUSIVOS</h3>
              <p className="panel-description">
                Hemos preparado materiales especiales para ayudarte a mejorar tu bienestar emocional.
              </p>
            </div>
            
            <div className="resources-container">
              <div className="resource-card main-guide" style={{ borderColor: results.color + '40' }}>
                <div className="card-header" style={{ backgroundColor: results.color + '15' }}>
                  <div className="resource-icon" style={{ backgroundColor: results.color }}>
                    <span>📒</span>
                  </div>
                  <h4>{areaInfo.name === 'Ansiedad' ? 'Guía Completa para Superar la Ansiedad' : 
                     areaInfo.name === 'Estrés' ? 'Guía Completa para Manejar el Estrés' : 
                     areaInfo.name === 'Tristeza' ? 'Guía Completa para Superar la Tristeza' : 
                     areaInfo.name === 'Autoestima' ? 'Guía Completa para Fortalecer tu Autoestima' : 
                     areaInfo.name === 'Ruptura' ? 'Guía Completa para Superar una Ruptura' : 
                     areaInfo.name === 'Insomnio' ? 'Guía Completa para Recuperar tu Sueño' : 
                     'Guía Completa de Autogestión Emocional'}</h4>
                </div>
                
                <div className="resource-content">
                  <ul className="resource-features modern">
                    <li><span className="feature-check">✓</span> 15 páginas con estrategias específicas para {areaInfo.name.toLowerCase()}</li>
                    <li><span className="feature-check">✓</span> Ejercicios paso a paso diseñados por psicólogos especializados</li>
                    <li><span className="feature-check">✓</span> Herramientas sencillas y prácticas de aplicación inmediata</li>
                  </ul>
                  
                  <div className="resource-value modern">
                    <div className="value-badge">VALOR: <span className="price">€45</span></div>
                    <div className="free-badge">GRATIS AHORA</div>
                  </div>
                </div>
              </div>
              
              <div className="bonus-section">
                <div className="bonus-header">
                  <span className="bonus-label">BONUS INCLUIDOS</span>
                </div>
                
                <div className="bonus-cards-container">
                  <div className="resource-card bonus-card">
                    <div className="card-header bonus-header-card">
                      <div className="resource-icon audio-icon">
                        <span>🎧</span>
                      </div>
                      <h4>Meditación Guiada</h4>
                    </div>
                    
                    <div className="resource-content">
                      <ul className="resource-features compact">
                        <li><span className="feature-check">✓</span> Audio de 10 minutos para regulación emocional</li>
                        <li><span className="feature-check">✓</span> Técnicas efectivas para reducir el malestar</li>
                      </ul>
                      
                      <div className="resource-value included">
                        <span className="value-mini">Valor €25</span>
                        <span className="included-mini">INCLUIDO</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="resource-card bonus-card">
                    <div className="card-header bonus-header-card">
                      <div className="resource-icon library-icon">
                        <span>🔍</span>
                      </div>
                      <h4>Ejercicios Interactivos</h4>
                    </div>
                    
                    <div className="resource-content">
                      <ul className="resource-features compact">
                        <li><span className="feature-check">✓</span> 18 tarjetas con ejercicios prácticos</li>
                        <li><span className="feature-check">✓</span> Recursos para diferentes síntomas</li>
                      </ul>
                      
                      <div className="resource-value included">
                        <span className="value-mini">Valor €35</span>
                        <span className="included-mini">INCLUIDO</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="lead-form-wrapper">
                <div className="lead-header">
                  <div className="total-value-modern">
                    <span className="value-label">Valor Total:</span>
                    <span className="total-price">€105</span>
                    <span className="free-now">GRATIS AHORA</span>
                  </div>
                  
                  <div className="limited-time">
                    <span className="timer-icon">⏱️</span>
                    <span>Oferta por tiempo limitado</span>
                  </div>
                </div>
                
                <div className="lead-form-container modern">
                  <h5>¡Obtén estos recursos exclusivos!</h5>
                  <LeadForm areaId={selectedArea} />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      
      {/* Contacto de emergencia */}
      {showEmergency && (
        <motion.div 
          className="emergency-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          <div className="emergency-content">
            <div className="emergency-header">
              <div className="emergency-icon">
                <span>❗</span>
              </div>
              <h3>Si experimentas un nivel intenso de sufrimiento</h3>
            </div>
            
            <p className="emergency-message">
              Recuerda que tu vida vale más que cualquier dificultad que estés atravesando. 
              No estás solo/a, hay profesionales dispuestos a escucharte y ayudarte 24/7.
            </p>
            
            <a href="tel:024" className="emergency-button pulse-animation">
              <span className="phone-icon">📞</span>
              <span className="call-text">Línea de Prevención del Suicidio</span>
              <span className="phone-number">024</span>
              <span className="service-label">Atención gratuita 24/7</span>
            </a>
          </div>
        </motion.div>
      )}
      
      <div className="restart-button-container">
        <button 
          className="restart-button"
          onClick={handleStartOver}
        >
          Realizar nueva evaluación
        </button>
      </div>
    </div>
  );
};

export default Results;
